import { RouteComponentProps } from 'react-router-dom';
import Page from '../../components/Page';
import PageLayout from '../../components/PageLayout/PageLayout';
import PageTitle from '../../components/PageLayout/PageTitle';
import { useCatalogs } from '../../hooks';
import history from '../../history';
import * as paths from '../../routes/paths';
import LoadingModal from '@raydiant/raydial/components/LoadingModal';
import Card from '@raydiant/raydial/components/Card';
import Text from '@raydiant/raydial/components/Text';
import TextField from '@raydiant/raydial/components/TextField';
import { useEffect, useState } from 'react';
import Select, { SelectItem } from '@raydiant/raydial/components/Select';
import { Footer } from './components';
import Button from '@raydiant/raydial/components/Button';
import useUpdateCatalog from '../../hooks/useUpdateCatalog';
import { useToastState } from '@raydiant/raydial/components/Toast';
import config from '../../config';

type RouteParams = {
  catalogId: string;
};

type CatalogSettings = {
  authId: string;
  businessId: string;
};

type LightspeedBusinessLocations = {
  locations: { id: number; name: string }[];
};

const ProductHubEditCataloguePage = (
  props: RouteComponentProps<RouteParams>,
) => {
  const { params } = props.match;
  const { catalogId } = params;
  const catalogsQuery = useCatalogs();
  const state = useToastState();
  const [isFetchingLightspeedBusinesses, setIsFetchingBusinesses] =
    useState(false);
  const [selectedLightspeedBusinessName, setSelectedLightspeedBusinessName] =
    useState('');
  const [catalogName, setCatalogName] = useState('');
  const updateCatalogQuery = useUpdateCatalog();
  const { mutateAsync: updateCatalog } = updateCatalogQuery;

  useEffect(() => {
    if (!catalogsQuery.data || catalogsQuery.isLoading) {
      return;
    }

    const { data: catalogs } = catalogsQuery.data;
    const catalog = catalogs.find((c) => c.id === catalogId)!;
    setCatalogName(catalog?.name || '');

    const fetchLightspeedBusinesses = async () => {
      try {
        setIsFetchingBusinesses(true);

        const catalogSettings = catalog?.settings as CatalogSettings;
        const { businessId, authId } = catalogSettings;
        const lightspeedBusinessesUrl = `${config.lightspeedConnectorApiUrl}/businesses?authId=${authId}`;

        const response = await fetch(lightspeedBusinessesUrl);
        const data: LightspeedBusinessLocations = await response.json();
        const { locations } = data;

        const selectedBusiness = locations.find(
          (location) => location.id.toString() === businessId,
        )!;

        setSelectedLightspeedBusinessName(selectedBusiness.name);
        setIsFetchingBusinesses(false);
      } catch (error) {
        console.error(error);
      }
    };

    if (catalog.connection?.connector.name === 'lightspeed') {
      fetchLightspeedBusinesses();
    }
  }, [catalogsQuery.data, catalogId, catalogsQuery.isLoading]);

  if (
    catalogsQuery.isLoading ||
    updateCatalogQuery.isLoading ||
    isFetchingLightspeedBusinesses
  ) {
    return (
      <Page>
        <LoadingModal isOpen={true} />
      </Page>
    );
  }

  if (!catalogsQuery.data) {
    return null;
  }

  const { data: catalogs } = catalogsQuery.data;

  const catalog = catalogs.find((c) => c.id === catalogId)!;

  const editCatalog = async () => {
    await updateCatalog({
      id: catalog.id,
      params: {
        name: catalogName,
      },
    });

    state.add({
      title: 'Catalog Updated',
      description: 'Catalog was updated successfully',
    });
    history.push(paths.catalogs());
  };

  const handleSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
    e.preventDefault();
    editCatalog();
  };

  const onCancel = () => {
    history.push(paths.catalogs());
  };

  if (!catalog) {
    return null;
  }

  return (
    <Page>
      <PageLayout title={<PageTitle title="Catalogs" />} className="text-black">
        <Card className="max-w-[628px]">
          <form onSubmit={handleSubmit}>
            <header className="flex items-center px-4">
              <div className="grid gap-1 py-4 mr-auto">
                <Text as="h2" variant="subheader">
                  Edit Catalog
                </Text>
                <Text as="h3" className="text-gray-400 font-regular text-lg">
                  Edit Catalog Name
                </Text>
              </div>
            </header>

            <div className="flex flex-col gap-4 mb-4">
              <TextField
                value={catalogName}
                onChange={setCatalogName}
                label="Catalog Name"
                description="This could be your restaurant or brand, eg: The Burger Barn"
              />

              <Select
                isDisabled
                selectedKey="connection-name"
                label="Integration for catalog"
              >
                <SelectItem id="connection-name" key="connection-name">
                  {catalog.connection?.connector.displayName}
                  {catalog.connection?.name
                    ? ` — ${catalog.connection.name}`
                    : ''}
                </SelectItem>
              </Select>
              {selectedLightspeedBusinessName && (
                <Select
                  isDisabled
                  selectedKey="business-name"
                  label="Lightspeed Business"
                >
                  <SelectItem id="business-name" key="business-name">
                    {selectedLightspeedBusinessName}
                  </SelectItem>
                </Select>
              )}
            </div>

            <Footer>
              <Button
                size="large"
                variant="secondary"
                className="flex-1"
                onPress={onCancel}
              >
                Cancel
              </Button>
              <Button
                size="large"
                variant="primary"
                className="flex-1"
                type="submit"
              >
                Save
              </Button>
            </Footer>
          </form>
        </Card>
      </PageLayout>
    </Page>
  );
};

export default ProductHubEditCataloguePage;
